import React, { useEffect, useState, forwardRef, useMemo } from "react";
import cn from "classnames";
import OutsideClickHandler from "react-outside-click-handler";
import { shortenAddress } from "../../../utils";
import styles from "./Network.module.sass";
import { useDisconnect } from "wagmi";
import useConfig from "../../../customHooks/useConfig";
import { getActiveChains, CASPER_SWITCH } from "../../../utils/config";
import { useAccount, useSwitchNetwork } from "wagmi";
import { useToastNotification } from "../../../customHooks/useToastNotification";
import Icon from "../../Icon";
import { useDispatch, useSelector } from "react-redux";
import { setChainId } from "../../../redux/tokenSlice";
import useCasperWallet from '../../../customHooks/useCasperWallet';
import { setCasperChainConfig } from "../../../redux/casperSlice";
import { useLocation } from "react-router-dom";
import Tooltip from "../../Tooltip";
import { PiUserSwitch } from "react-icons/pi";

const Network = forwardRef(({ className, onSelectedChainChange }, ref) => {
    const clickCasper = window.csprclick;
    const config = useConfig();
    const { isConnected } = useAccount();
    const dispatch = useDispatch();
    const { disconnect } = useDisconnect();
    const { ensName, address } = useSelector((state) => state.settings);
    const casperAccount = useSelector((state) => state.casper.casperActiveAccount);
    const [visible, setVisible] = useState(false);
    const {
        error,
        isSuccess
    } = useSwitchNetwork();
    const toastNotification = useToastNotification();
    const location = useLocation();
    const activeChains = useMemo(() => {
        const chains = getActiveChains(location.pathname);
        return CASPER_SWITCH ? chains : chains.filter(chain => chain.chainNetwork !== 'casper');
    }, [location.pathname]);
    const [selectedChain, setSelectedChain] = useState();
    const [lastEVMChainId, setLastEVMChainId] = useState(() => {
        // Initialize from localStorage if available
        const stored = localStorage.getItem('lastEVMChainId');
        return stored ? parseInt(stored, 10) : null;
    });
    const {
        error: casperError,
    } = useCasperWallet(clickCasper);

    const casperPubKey = casperAccount?.public_key || null;

    const isCasperConnected = !!casperAccount;
    
    // Memoize Casper config to prevent infinite loops
    const casperChainConfig = useMemo(() => {
        return activeChains.find(c => c.chainNetwork === 'casper');
    }, [activeChains]);

    const casperDefaultNetworkName = casperChainConfig?.defaultNetworkName;
    const {
        chainId,
        chainIcon
    } = config;

    useEffect(() => {
        if (casperChainConfig) {
            dispatch(setCasperChainConfig(casperChainConfig));
        }
    }, [casperChainConfig, dispatch]);

    // Save lastEVMChainId to localStorage whenever it changes
    useEffect(() => {
        if (lastEVMChainId) {
            localStorage.setItem('lastEVMChainId', lastEVMChainId.toString());
        }
    }, [lastEVMChainId]);

    // Listen for network modal open event from ChainSupportCloak
    useEffect(() => {
        const handleOpenNetworkModal = () => {
            setVisible(true);
        };

        window.addEventListener('openNetworkModal', handleOpenNetworkModal);
        
        return () => {
            window.removeEventListener('openNetworkModal', handleOpenNetworkModal);
        };
    }, []);

    useEffect(() => {
        if (error) {
            toastNotification.warning(
                error.message
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error]);

    useEffect(() => {
        if (casperError) {
            toastNotification.warning(casperError.message || casperError.toString());
        }
    }, [casperError, toastNotification]);

    useEffect(() => {
        if (isSuccess) {
            toastNotification.success(
                `Successfully switched to ${selectedChain.chainName}`
            );

            dispatch(
                setChainId(
                    selectedChain.chainId
                )
            );
            // setVisible(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuccess]);

    useEffect(() => {
        // If Casper is selected, clear Redux address on chain switch
        if (selectedChain && selectedChain.chainNetwork === 'casper') {
            // dispatch(setAddress(null));
            return;
        }
    }, [selectedChain, dispatch, casperDefaultNetworkName]);

    useEffect(() => {
        if (onSelectedChainChange) {
            onSelectedChainChange(selectedChain);
        }
    }, [selectedChain, onSelectedChainChange]);

    // Show toast when Casper wallet is connected (moved out of connectWallet)
    useEffect(() => {
        if (
            selectedChain &&
            selectedChain.chainNetwork === 'casper' &&
            isCasperConnected &&
            casperPubKey
        ) {
            toastNotification.success('Connected to Casper Wallet');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCasperConnected, casperPubKey, selectedChain]);

    // Simple sync of selectedChain with current chainId
    useEffect(() => {
        if (chainId) {
            const currentChain = activeChains.find(chain => chain.chainId === chainId);
            if (currentChain) {
                setSelectedChain(currentChain);
            }
        }
    }, [chainId, activeChains]);

    // Track EVM chain changes for localStorage
    useEffect(() => {
        if (chainId && chainId !== 'casper-test' && typeof chainId === 'number') {
            setLastEVMChainId(chainId);
        }
    }, [chainId]);

    const handleSwitch = async (chain) => {
        // Don't allow switching to disabled chains
        if (chain.isDisabled) {
            return;
        }

        // Track EVM chain selections
        if (chain.chainNetwork !== 'casper') {
            setLastEVMChainId(chain.chainId);
        }

        setSelectedChain(chain);
        if (onSelectedChainChange) {
            onSelectedChainChange(chain);
        }
        if (chain.chainNetwork === 'casper') {
            // Disconnect EVM wallet if connected
            if (isConnected) {
                disconnect();
            }
            dispatch(setChainId(chain.chainId));
            setVisible(false);
            return;
        } else {
            // Disconnect Casper wallet if connected
            if (isCasperConnected) {
                handleCasperDisconnect();
            }
            dispatch(setChainId(chain.chainId));
            setVisible(false);
        }
    };

    const handleCasperDisconnect = async () => {
        await clickCasper?.signOut();
        toastNotification.success('Disconnected Casper Wallet');
        setVisible(false);
    };

    const handleSwitchAccount = async () => {
        try {
            await clickCasper?.switchAccount();
        } catch (error) {
            console.error('Error switching account:', error);
            toastNotification.warning('Failed to switch account');
        }
    };

    return (
        <OutsideClickHandler onOutsideClick={() => setVisible(false)}>
            <div className={cn(
                styles.pointer,
                styles.network,
                className,
                {
                    [styles.active]: visible
                }
            )}>
                <div className={styles.head} onClick={() => setVisible(!visible)}>
                    <div className={cn(
                        styles.wallet
                    )}>
                        {
                            selectedChain && selectedChain.chainNetwork === 'casper' ? (
                                <>
                                    <div className={cn({
                                        [styles.chainIcon]: isCasperConnected
                                    })}>
                                        <img
                                            src={casperChainConfig?.chainIcon}
                                            className={styles.icon}
                                            alt="network icon"
                                            width={30}
                                            height={30}
                                        />
                                    </div>
                                    <span className={styles.chainLabel}>
                                        {casperPubKey
                                            ? ` (${casperPubKey.slice(0, 6)}...${casperPubKey.slice(-6)})`
                                            : selectedChain.chainName }
                                    </span>
                                    <Icon
                                        name="arrow-down"
                                        size="20"
                                        className={cn(
                                            styles.svgIcon,
                                            {
                                                [styles.active]: visible
                                            }
                                        )}
                                    />
                                </>
                            ) : (
                                chainId &&
                                <>
                                    <div className={cn({
                                        [styles.chainIcon]: isConnected
                                    })}>
                                        <img
                                            src={chainIcon}
                                            className={styles.icon}
                                            alt="network icon"
                                            width={30}
                                            height={30}
                                        />
                                    </div>
                                    <span className={styles.chainLabel}>
                                        {/*chainName*/}
                                        {ensName ?? shortenAddress(address)}
                                    </span>
                                    <Icon
                                        name="arrow-down"
                                        size="20"
                                        className={cn(
                                            styles.svgIcon,
                                            {
                                                [styles.active]: visible
                                            }
                                        )}
                                    />
                                </>
                            )
                        }
                    </div>
                    <div style={{ height: "50px" }}></div>
                </div>
                <div className={cn(
                    styles.body,
                    {
                        [styles.customBodyMarginRight]: isConnected || isCasperConnected
                    },
                    {
                        [styles.expand]: isConnected || isCasperConnected
                    }
                )}>
                    {activeChains.map((item, index) => (
                        <Tooltip
                            key={index}
                            title={item.isDisabled ? item.disabledReason : null}
                            place="right"
                            disabled={!item.isDisabled}
                        >
                            <button
                                className={cn(
                                    styles.item,
                                    {
                                        [styles.disabled]: item.isDisabled
                                    }
                                )}
                                onClick={() => handleSwitch(item)}
                                disabled={item.isDisabled}
                            >
                                <div className={styles.chain}>
                                    <img
                                        src={item.chainIcon}
                                        className={cn(
                                            styles.icon,
                                            {
                                                [styles.disabledIcon]: item.isDisabled
                                            }
                                        )}
                                        alt="network icon"
                                        width={24}
                                        height={24}
                                    />
                                    {item.chainName}
                                </div>
                                {
                                    (chainId === item.chainId && isConnected && item.chainNetwork !== 'casper') ||
                                    (item.chainNetwork === 'casper' && isCasperConnected && location.pathname === '/liquid-staking') ? (
                                        <div className={styles.connected}>
                                            Online
                                        </div>
                                    ) : null
                                }
                            </button>
                        </Tooltip>
                    ))}
                    {/* Casper-specific actions when connected */}
                    {selectedChain && selectedChain.chainNetwork === 'casper' && isCasperConnected && (
                        <>
                            <button
                                className={styles.item}
                                onClick={handleSwitchAccount}
                            >
                                <div className={styles.chain}>
                                    <PiUserSwitch size="24" />
                                    Switch Account
                                </div>
                            </button>
                            <button
                                className={styles.item}
                                onClick={handleCasperDisconnect}
                            >
                                <div className={styles.chain}>
                                    <Icon name={"close"} size="24" />
                                    Disconnect Casper Wallet
                                </div>
                            </button>
                        </>
                    )}
                    {/* EVM wallet disconnect when connected */}
                    {isConnected && selectedChain && selectedChain.chainNetwork !== 'casper' && (
                        <button
                            className={styles.item}
                            onClick={() => {
                                // Disconnect EVM wallet
                                disconnect();
                                // dispatch(setAddress(null));
                            }}
                        >
                            <div className={styles.chain}>
                                <Icon name={"close"} size="24" />
                                Disconnect Wallet
                            </div>
                        </button>
                    )}
                </div>
            </div>
        </OutsideClickHandler>
    );
});

export default Network;
